import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ensureSessionAuthFromLocal } from "@/lib/auth-storage";
import { ensureDevSession } from "@/lib/dev-auth";
import {
  canAccessPathname,
  getFirstAllowedAppHome,
  getStoredApplicationAccess,
} from "@/utils/app-route.utils";
import {
  canAccessExtrasPath,
  getStoredRoleCode,
} from "@/utils/role.utils";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Local dev bypass seeds a fake session; see src/lib/dev-auth.ts
    if (ensureDevSession()) {
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }
    // Prefer shared localStorage; hydrate sessionStorage for existing readers
    setIsAuthenticated(ensureSessionAuthFromLocal());
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?redirect_uri=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  const accessList = getStoredApplicationAccess();
  if (!canAccessPathname(location.pathname, accessList)) {
    const fallback = getFirstAllowedAppHome(accessList);
    return <Navigate to={fallback} replace />;
  }

  // Same rule as sidebar Extras hide: MERCHANT_ADMIN / PROGRAM_ADMIN cannot open these URLs
  if (!canAccessExtrasPath(location.pathname, getStoredRoleCode())) {
    return <Navigate to={getFirstAllowedAppHome(accessList)} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
